import React, { useState, useEffect, useRef } from 'react';
import { useParams, Link } from 'react-router-dom';
import { doc, getDoc } from 'firebase/firestore';
import { db } from './firebaseConfig';
import './TalentCardView.css';
import { useTranslation } from 'react-i18next';
import html2pdf from 'html2pdf.js';

function TalentCardView() {
  const { t } = useTranslation();
  const [talentCard, setTalentCard] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const { id } = useParams();
  const talentCardRef = useRef(null);

  useEffect(() => {
    async function fetchTalentCard() {
      try {
        const talentCardDoc = await getDoc(doc(db, 'talentCards', id));
        if (talentCardDoc.exists()) {
          const data = talentCardDoc.data();
          // Remove sensitive fields before setting the state
          const { userId, sessionId, ...publicData } = data;
          setTalentCard(publicData);
        } else {
          setError(t('talentCardNotFound'));
        }
      } catch (err) {
        setError(t('errorFetchingTalentCard'));
        console.error(err);
      } finally {
        setLoading(false);
      }
    }

    fetchTalentCard();
  }, [id, t]);

  const exportToPdf = () => {
    const talentCardUrl = window.location.href;
    const {
      name, title, summary, idealWorkEnvironment, skills, experience, education,
      strengths, availability, locationPreferences, financialExpectations, values,
      internships, certifications, languages, createdAt, updatedAt
    } = talentCard;

    const formatDate = (timestamp) => {
      if (!timestamp || !timestamp.toDate) return 'N/A';
      return timestamp.toDate().toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' });
    };

    // --- Helper functions for rendering sections ---
    const renderSidebarSection = (title, content) => {
      if (!content) return '';
      return `<div class="sidebar-section"><h2>${t(title, { defaultValue: title })}</h2>${content}</div>`;
    };
    const renderMainSection = (title, content) => {
      if (!content) return '';
      return `<div class="main-section"><h2>${t(title, { defaultValue: title })}</h2>${content}</div>`;
    };

    const renderText = (value) => `<p>${value}</p>`;
    const renderSimpleList = (items) => `<ul>${items.map(i => `<li>${i}</li>`).join('')}</ul>`;
    const renderExperience = (exps) => exps.map(exp => `
      <div class="item">
        <h3>${exp.role || ''} at ${exp.company || ''}</h3>
        <p class="subheading">${exp.duration || ''}</p>
        ${exp.highlights && exp.highlights.length > 0 ? `<ul>${exp.highlights.map(h => `<li>${h}</li>`).join('')}</ul>` : ''}
      </div>`).join('');
    const renderEducation = (edus) => edus.map(edu => `
      <div class="item">
        <h3>${edu.degree || ''}</h3>
        <p class="subheading">${edu.institution || ''} | ${edu.year || ''}</p>
      </div>`).join('');

    // --- Define content for each column ---
    const sidebarOrder = ['skills', 'values', 'strengths', 'languages', 'certifications'];
    const mainOrder = ['summary', 'idealWorkEnvironment', 'availability', 'locationPreferences', 'financialExpectations', 'experience', 'education', 'internships'];

    const knownSections = {
      summary: { title: 'Summary', render: renderText, column: 'main' },
      skills: { title: 'Top Skills', render: renderSimpleList, column: 'sidebar' },
      values: { title: 'Values', render: renderSimpleList, column: 'sidebar' },
      strengths: { title: 'Strengths', render: renderSimpleList, column: 'sidebar' },
      idealWorkEnvironment: { title: 'Ideal Work Environment', render: renderText, column: 'main' },
      availability: { title: 'Availability', render: renderText, column: 'main' },
      locationPreferences: { title: 'Location Preferences', render: renderText, column: 'main' },
      financialExpectations: { title: 'Financial Expectations', render: renderText, column: 'main' },
      experience: { title: 'Experience', render: renderExperience, column: 'main' },
      education: { title: 'Education', render: renderEducation, column: 'main' },
      internships: { title: 'Internships', render: (items) => items.map(i => `<div class="item"><h3>${i.company}</h3><p>${i.details}</p></div>`).join(''), column: 'main' },
      certifications: { title: 'Certifications', render: (items) => renderSimpleList(items.map(c => c.name || c)), column: 'sidebar' },
      languages: { title: 'Languages', render: renderSimpleList, column: 'sidebar' },
    };

    let sidebarHtml = '';
    let mainHtml = '';

    const renderOrderedSections = (order, column) => {
      let html = '';
      order.forEach(key => {
        if (talentCard[key] && (Array.isArray(talentCard[key]) ? talentCard[key].length > 0 : true)) {
          const config = knownSections[key];
          if (config && config.column === column) {
            html += (column === 'sidebar')
              ? renderSidebarSection(config.title, config.render(talentCard[key]))
              : renderMainSection(config.title, config.render(talentCard[key]));
          }
        }
      });
      return html;
    };

    sidebarHtml = renderOrderedSections(sidebarOrder, 'sidebar');
    mainHtml = renderOrderedSections(mainOrder, 'main');

    const professionalTemplate = `
      <html>
        <head>
          <style>
            body { font-family: 'Helvetica Neue', Helvetica, Arial, sans-serif; font-size: 10pt; line-height: 1.5; color: #333; }
            .page { display: flex; }
            .sidebar { width: 220px; background-color: #2d3748; color: #fff; padding: 30px; }
            .main-content { flex-grow: 1; padding: 30px; }
            .sidebar-section { margin-bottom: 25px; break-inside: avoid; }
            .sidebar-section h2 { font-size: 12pt; color: #fff; border-bottom: 1px solid #4a5568; padding-bottom: 8px; margin: 0 0 15px; }
            .sidebar-section ul { padding-left: 18px; margin: 0; }
            .sidebar-section li { margin-bottom: 6px; font-size: 9pt; }
            .main-header { margin-bottom: 30px; background-color: #f8f9fa; padding: 25px; border-radius: 8px; }
            .main-header h1 { font-size: 28pt; margin: 0; color: #4A5568; }
            .main-header p { font-size: 14pt; color: #718096; margin: 5px 0 0; }
            .main-section { margin-bottom: 30px; break-inside: avoid; }
            .main-section h2 { font-size: 16pt; color: #2d3748; border-bottom: 1px solid #ccc; padding-bottom: 10px; margin: 0 0 20px; }
            .main-section p, .main-section ul { color: #555; }
            .item { margin-bottom: 20px; }
            h3 { font-size: 12pt; margin: 0 0 2px; color: #333; }
            .subheading { font-size: 10pt; color: #555; margin: 0 0 10px; }
            .footer { font-size: 8pt; text-align: center; margin-top: 30px; color: #aaa; }
          </style>
        </head>
        <body>
          <div class="page">
            <div class="sidebar">
              ${sidebarHtml}
            </div>
            <div class="main-content">
              <div class="main-header">
                <h1>${name || ''}</h1>
                <p>${title || ''}</p>
              </div>
              ${mainHtml}
            </div>
          </div>
           <div class="footer">
              <p>Created: ${formatDate(createdAt)} | Last Updated: ${formatDate(updatedAt)}</p>
              <p>${t('pdfFootnote')} <a href="${talentCardUrl}">${talentCardUrl}</a></p>
            </div>
        </body>
      </html>
    `;

    const opt = {
      margin:       [0.7, 0, 0.7, 0],
      filename:     'talent-card.pdf',
      image:        { type: 'jpeg', quality: 0.98 },
      html2canvas:  { scale: 2 },
      jsPDF:        { unit: 'in', format: 'letter', orientation: 'portrait' }
    };

    html2pdf().from(professionalTemplate).set(opt).save();
  };

  if (loading) return <div className="talent-card-view loading">{t('loading')}</div>;
  if (error) return <div className="talent-card-view error">{error}</div>;
  if (!talentCard) return <div className="talent-card-view not-found">{t('noTalentCardFound')}</div>;

  // Function to format timestamps
  const formatDate = (timestamp) => {
    if (!timestamp) return 'N/A';
    const date = timestamp.toDate();
    return date.toLocaleString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
      timeZoneName: 'short'
    });
  };

  return (
    <div className="container" ref={talentCardRef}>
      <div className="profile-page-actions no-print">
        <Link to="/welcome" className="btn btn-secondary">← {t('backToHome')}</Link>
      </div>
      <header className="profile-header">
        <div>
          <h1>{talentCard.name !== "Your Name" ? talentCard.name : t('talentCard')}</h1>
          <p>{talentCard.title}</p>
        </div>
        <div className="actions no-print">
          <Link to={`/digital-twin/${id}`} className="icon-btn digital-twin-btn" title={t('talkToTwin')}>
            <svg 
              xmlns="http://www.w3.org/2000/svg" 
              width="24" 
              height="24" 
              viewBox="0 0 24 24" 
              fill="none" 
              stroke="#4285F4" 
              strokeWidth="2" 
              strokeLinecap="round" 
              strokeLinejoin="round"
              style={{ position: 'relative', zIndex: 10 }}
            >
              <rect x="2" y="7" width="20" height="14" rx="2" ry="2"></rect>
              <path d="M6 7V5a2 2 0 0 1 2-2h8a2 2 0 0 1 2 2v2"></path>
              <circle cx="8" cy="14" r="1"></circle>
              <circle cx="16" cy="14" r="1"></circle>
            </svg>
          </Link>
          <button onClick={exportToPdf} className="btn-export">{t('exportToPdf')}</button>
        </div>
      </header>

      <div className="profile-body">
        <aside className="profile-sidebar">
          {talentCard.skills && talentCard.skills.length > 0 && (
            <div className="section">
              <h3>{t('skills')}</h3>
              <div className="skills-list">
                {talentCard.skills.map((skill, index) => (
                  <span key={index} className="skill-tag">{skill}</span>
                ))}
              </div>
            </div>
          )}
          <div className="section">
            <h3>Details</h3>
            <ul className="info-list">
              {talentCard.availability && <li><strong>{t('availability')}</strong><span>{talentCard.availability}</span></li>}
              {talentCard.locationPreferences && <li><strong>{t('locationPreferences')}</strong><span>{talentCard.locationPreferences}</span></li>}
              {talentCard.financialExpectations && <li><strong>{t('financialExpectations')}</strong><span>{talentCard.financialExpectations}</span></li>}
              {talentCard.values && talentCard.values.length > 0 && <li><strong>{t('values')}</strong><span>{talentCard.values.join(', ')}</span></li>}
            </ul>
          </div>
          {talentCard.strengths && talentCard.strengths.length > 0 && (
            <div className="section">
              <h3>{t('strengths')}</h3>
              <ul className="info-list">
                {talentCard.strengths.map((strength, index) => (
                  <li key={index}>{strength}</li>
                ))}
              </ul>
            </div>
          )}
        </aside>

        <main className="profile-main">
          <div className="section">
            <h3>{t('professionalSummary')}</h3>
            <p>{talentCard.summary}</p>
          </div>

          {talentCard.idealWorkEnvironment && (
            <div className="section">
              <h3>{t('idealWorkEnvironment')}</h3>
              <p>{talentCard.idealWorkEnvironment}</p>
            </div>
          )}

          {talentCard.experience && talentCard.experience.length > 0 && (
            <div className="section">
              <h3>{t('workExperience')}</h3>
              {talentCard.experience.map((exp, index) => (
                <div key={index} className="experience-item">
                  <h4>{exp.role}</h4>
                  <p className="company">{exp.company} | {exp.duration}</p>
                  {exp.highlights && exp.highlights.length > 0 && (
                    <ul>
                      {exp.highlights.map((highlight, i) => (
                        <li key={i}>{highlight}</li>
                      ))}
                    </ul>
                  )}
                </div>
              ))}
            </div>
          )}

          {talentCard.education && talentCard.education.length > 0 && (
            <div className="section">
              <h3>{t('education')}</h3>
              {talentCard.education.map((edu, index) => (
                <div key={index} className="experience-item">
                  <h4>{edu.degree}</h4>
                  <p className="company">{edu.institution} | {edu.year}</p>
                  {edu.details && <p>{edu.details}</p>}
                </div>
              ))}
            </div>
          )}

          {talentCard.internships && talentCard.internships.length > 0 && (
            <div className="section">
              <h3>{t('internships')}</h3>
              {talentCard.internships.map((internship, index) => (
                <div key={index} className="experience-item">
                  <h4>{internship.company}</h4>
                  <p>{internship.details}</p>
                </div>
              ))}
            </div>
          )}

          {talentCard.certifications && talentCard.certifications.length > 0 && (
            <div className="section">
              <h3>{t('certifications')}</h3>
              <ul className="info-list">
                {talentCard.certifications.map((cert, index) => (
                  <li key={index}>
                    {typeof cert === 'string' ? cert : (cert.url ? <a href={cert.url} target="_blank" rel="noopener noreferrer">{cert.name}</a> : cert.name)}
                  </li>
                ))}
              </ul>
            </div>
          )}

          {talentCard.languages && talentCard.languages.length > 0 && (
            <div className="section">
              <h3>{t('languages')}</h3>
              <ul className="info-list">
                {talentCard.languages.map((lang, index) => (
                  <li key={index}>{lang}</li>
                ))}
              </ul>
            </div>
          )}

          <div className="section timestamps">
            <p><strong>{t('created')}:</strong> {formatDate(talentCard.createdAt)}</p>
            {talentCard.updatedAt && (
              <p><strong>{t('lastUpdated')}:</strong> {formatDate(talentCard.updatedAt)}</p>
            )}
          </div>
        </main>
      </div>
    </div>
  );
}

export default TalentCardView;